import { Alert } from '@designsforhealth/dfh-react-components'
import { CircularProgress } from '@material-ui/core'
import { Subscribe } from '@react-rxjs/core'
import React, { useCallback, useMemo } from 'react'
import { FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { first, firstValueFrom, map, skipWhile } from 'rxjs'

import {
  SanityContact,
  SanityDispensaryDocument,
} from '../../../../../../groq/sanity-dispensaries/types'
import withDispensaryAccount, {
  DispensaryAccountPageProps,
} from '../../../../../../hoc/withDispensaryAccount'
import { useLogAndCaptureLeftResult } from '../../../../../../hooks/useLogAndCaptureLeftResult'
import { useResultWithLeftEffect } from '../../../../../../hooks/useResultWithLeftEffect'
import useSiteSettings from '../../../../../../hooks/useSiteSettings'
import { Dispensary } from '../../../../../../lib/dispensaries'
import { countryOptionsFor } from '../../../../../../lib/form/address'
import { emailValidationRule } from '../../../../../../lib/form/email'
import {
  sanityDispensaryUpdate$,
  setUpdateDispensaryInput,
  useSanityDispensaryGetOrCreate,
  useSanityDispensaryUpdate,
} from '../../../../../../lib/sanity-dispensaries/manage/dispensary'
import { constEmptyResult, constUndefined } from '../../../../../../utils/constant'
import { ModalActionButton, ModalMessage } from '../../../../../buy/checkout/modals/styled'
import ContactSupportMessage from '../../../../../ContactSupportMessage'
import { MaxLengthProgressAdornment } from '../../../../../form/MaxLengthProgress'
import { Switch } from '../../../../../form/Switch'
import TextField from '../../../../../form/TextField'
import Spacer from '../../../../../Spacer'
import Spinner from '../../../../../Spinner'
import ImageEditor from '../../customize/ImageEditor'
import { DispensaryAddressForm } from '../DispensaryAddressForm'
import { InputTooLongModal } from './InputTooLongModal'
import * as Styled from './styled'
import { TooLongItemList } from './TooLongItemList'

export interface DispensaryContactFormValues {
  specialty1: string
  specialty2: string
  specialty3: string
  blurb: string
  email: string
  phone: string
  street1: string
  street2: string
  city: string
  region: string
  postcode: string
  countryCode: string
  imageAssetRef: string
  hideCardInDispensary: boolean
}

const DispensaryContactCardLivePreview: React.VFC<{
  dispensary: Pick<Dispensary, 'name' | 'slug'>
}> = ({ dispensary }) => {
  const formValues = useWatch({})
  const preview = useMemo(
    (): SanityContact => ({
      _type: 'contact',
      specialties: [formValues.specialty1, formValues.specialty2, formValues.specialty3].filter(
        Boolean,
      ),
      blurb: formValues.blurb,
      email: formValues.email,
      phone: formValues.phone,
      address: {
        _type: 'address',
        street: [formValues.street1, formValues.street2].filter(Boolean),
        city: formValues.city,
        region: formValues.region,
        postcode: formValues.postcode,
        countryCode: formValues.countryCode,
      },
      image: formValues.imageAssetRef
        ? {
            _type: 'image',
            asset: {
              _type: 'reference',
              _ref: formValues.imageAssetRef,
            },
          }
        : null,
      hideCardInDispensary: null,
    }),
    [formValues],
  )
  return <Styled.DispensaryContactCardPrime dispensary={dispensary} contact={preview} />
}

const pluckData = <T,>({ data }: { data: T }) => data

const setNextUpdateInput = (data: DispensaryContactFormValues) => {
  setUpdateDispensaryInput({
    method: 'PUT',
    input: {
      contact: {
        specialties: [data.specialty1, data.specialty2, data.specialty3].filter(Boolean),
        blurb: data.blurb,
        email: data.email,
        phone: data.phone,
        address: {
          street: [data.street1, data.street2].filter(Boolean),
          city: data.city,
          region: data.region,
          postcode: data.postcode,
          countryCode: data.countryCode,
        },
        image: data.imageAssetRef
          ? {
              asset: {
                _ref: data.imageAssetRef,
              },
            }
          : undefined,
        hideCardInDispensary: data.hideCardInDispensary,
      },
    },
  })
}

const getDefaultValues = (
  sanityDispensary: SanityDispensaryDocument | undefined,
  defaultCountryCode: string,
) => {
  const { contact } = sanityDispensary ?? {}
  const { address, blurb, image, email, phone, specialties, hideCardInDispensary } = contact ?? {}
  return {
    specialty1: specialties?.[0] ?? '',
    specialty2: specialties?.[1] ?? '',
    specialty3: specialties?.[2] ?? '',
    blurb: blurb ?? '',
    email: email ?? '',
    phone: phone ?? '',
    street1: address?.street?.[0] ?? '',
    street2: address?.street?.[1] ?? '',
    city: address?.city ?? '',
    region: address?.region ?? '',
    postcode: address?.postcode ?? '',
    countryCode: address?.countryCode || defaultCountryCode,
    imageAssetRef: image?.asset?._ref ?? '',
    hideCardInDispensary: hideCardInDispensary ?? false,
  }
}

const DispensaryContactReactive: React.FC<Pick<DispensaryAccountPageProps, 'dispensary'>> = ({
  dispensary,
}) => {
  const [sanityDispensaryLeft, sanityDispensary] = useResultWithLeftEffect(
    useSanityDispensaryGetOrCreate(),
    pluckData,
    constUndefined,
  )

  const { loading: updateLoading, result: updateResult } = useSanityDispensaryUpdate()
  useLogAndCaptureLeftResult(updateResult ?? constEmptyResult())

  const { lang } = useSiteSettings() ?? {}

  const { options: countriesList, defaultOption: defaultCountryOption } = useMemo(
    () => countryOptionsFor(lang),
    [lang],
  )

  const defaultValues = useMemo(
    (): DispensaryContactFormValues =>
      getDefaultValues(sanityDispensary, defaultCountryOption.value),
    [sanityDispensary, defaultCountryOption.value],
  )

  const methods = useForm<DispensaryContactFormValues>({ defaultValues })
  const {
    formState: { isDirty, isSubmitting, errors },
    handleSubmit,
    register,
    reset,
    getValues,
    setValue,
    setError,
  } = methods

  const handleImageChange = useCallback(
    (assetRef: string) => {
      setValue('imageAssetRef', assetRef, { shouldDirty: true })
    },
    [setValue],
  )

  const [open, setOpen] = React.useState(false)
  const [overLengthFields, setOverLengthFields] = React.useState<string[]>([])

  React.useEffect(() => {
    // Set the errors in the use effect
    const fields: string[] = []
    const updatedFormValues = getValues()
    // console.log(updatedFormValues.specialty1.length)
    if (updatedFormValues?.specialty1 && updatedFormValues.specialty1.length > 50) {
      setError('specialty1', { message: 'Text may be too long', type: 'validate' })
      fields.push('Specialty 1')
    }
    if (updatedFormValues?.specialty2 && updatedFormValues.specialty2.length > 50) {
      setError('specialty2', { message: 'Text may be too long', type: 'validate' })
      fields.push('Specialty 2')
    }
    if (updatedFormValues?.specialty3 && updatedFormValues.specialty3.length > 50) {
      setError('specialty3', { message: 'Text may be too long', type: 'validate' })
      fields.push('Specialty 3')
    }
    if (updatedFormValues?.email && updatedFormValues.email.length > 50) {
      setError('email', { message: 'Text may be too long', type: 'validate' })
      fields.push('Email')
    }
    if (
      (updatedFormValues?.city?.length || 0) +
        (updatedFormValues?.street1?.length || 0) +
        (updatedFormValues?.street2?.length || 0) +
        (updatedFormValues?.countryCode?.length || 0) +
        (updatedFormValues?.postcode?.length || 0) +
        (updatedFormValues?.region?.length || 0) >
      75
    ) {
      setError('street1', { message: 'Total address text may be too long', type: 'validate' })
      fields.push('Address')
    }
    setOverLengthFields(fields)
  }, [getValues, setError, errors])

  const onSubmit: SubmitHandler<DispensaryContactFormValues> = useCallback(
    async (formValues: DispensaryContactFormValues): Promise<void> => {
      if (
        ((formValues?.specialty1 && formValues.specialty1.length > 50) ||
          (formValues?.specialty2 && formValues.specialty2.length > 50) ||
          (formValues?.specialty3 && formValues.specialty3.length > 50) ||
          (formValues?.email && formValues.email.length > 50) ||
          (formValues?.city?.length || 0) +
            (formValues?.street1?.length || 0) +
            (formValues?.street2?.length || 0) +
            (formValues?.countryCode?.length || 0) +
            (formValues?.postcode?.length || 0) +
            (formValues?.region?.length || 0) >
            75) &&
        !open
      ) {
        setOpen(true)
        return
      }
      setNextUpdateInput(formValues)
      setOpen(false)
      // raise error to react-hook-form if result === Left
      return firstValueFrom(
        sanityDispensaryUpdate$.pipe(
          // ignore any value that matches an existing result
          skipWhile(({ result }) => result === updateResult),
          // find the first notification where loading is false
          first(({ loading }) => !loading),
          map(({ result }) => {
            if (!result) {
              throw new Error('Upsert result not found')
            }
            if (result._tag === 'Left') {
              throw result.error
            }
            // reset form during submit completion instead of in useEffect to avoid
            // submit button flickering while isDirty is still true
            reset(getDefaultValues(result.data, defaultCountryOption.value))
          }),
        ),
      )
    },
    [updateResult, reset, defaultCountryOption.value, open],
  )

  const submitButton = useMemo(
    () => (
      <ModalActionButton type="submit" disabled={!isDirty || isSubmitting || updateLoading}>
        {isSubmitting || updateLoading ? (
          <CircularProgress size={20} color="inherit" />
        ) : isDirty ? (
          'Publish Changes'
        ) : (
          'No Changes to Publish'
        )}
      </ModalActionButton>
    ),
    [isDirty, isSubmitting, updateLoading],
  )

  const handleModalSubmit = useCallback(() => {
    handleSubmit(onSubmit)()
    setOpen(false)
  }, [handleSubmit, onSubmit])

  if (sanityDispensaryLeft ?? !sanityDispensary) {
    return (
      <div>
        <Alert type="error">
          An error occurred retrieving your dispensary customization, and we have been notified.{' '}
          <ContactSupportMessage />
        </Alert>
      </div>
    )
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Styled.HeaderButtonAndAbstract>
          <h3>Contact Information</h3>
          {submitButton}
          <Styled.Abstract>
            <p>
              Update your contact information and practice details to generate more interest in your
              virtual dispensary.
            </p>
            <p>
              Including your specialty, or a short description about yourself will help your virtual
              dispensary stand out and engage more new patients via the Designs for Health Patient
              Referral Program. All fields are optional, so you choose how much information to
              display.
            </p>
          </Styled.Abstract>
        </Styled.HeaderButtonAndAbstract>
        {updateResult?._tag === 'Left' && (
          <Alert type="error">
            An error occurred updating your dispensary, and we have been notified.{' '}
            <ContactSupportMessage />
          </Alert>
        )}
        <Styled.Heading>Preview</Styled.Heading>
        <DispensaryContactCardLivePreview dispensary={dispensary} />
        <Spacer size={3} />
        <Styled.FormGroup>
          <Switch
            label={
              <>
                <Styled.SwitchText>Hide my Contact Card</Styled.SwitchText>
                <Styled.SwitchSubText>
                  If checked, your patients will not see your Contact Card in your{' '}
                  <Styled.SwitchSubTextLink to={`/u/${dispensary.slug}/practitioner-about`}>
                    Virtual Dispensary&apos;s About
                  </Styled.SwitchSubTextLink>{' '}
                  section
                </Styled.SwitchSubText>
              </>
            }
            name="hideCardInDispensary"
            defaultValue={defaultValues.hideCardInDispensary}
          />
        </Styled.FormGroup>
        <Styled.FormDiv>
          <Styled.Heading>About You</Styled.Heading>
          <TextField label="Specialty 1" name="specialty1" />
          <TextField label="Specialty 2" name="specialty2" />
          <TextField label="Specialty 3" name="specialty3" />
          <TextField
            label="Short Description"
            name="blurb"
            rules={{ maxLength: { value: 180, message: 'Description exceeds maximum length' } }}
            multiline
            minRows={3}
            InputProps={{
              // allow adornment to display below input
              style: { flexWrap: 'wrap' },
              endAdornment: <MaxLengthProgressAdornment name="blurb" maxLength={180} />,
            }}
          />

          <Styled.Heading>Contact Information</Styled.Heading>
          <TextField
            autoComplete="email"
            type="email"
            label="Email"
            name="email"
            rules={emailValidationRule}
          />
          <TextField autoComplete="tel" type="tel" label="Phone number" name="phone" />
          <Styled.Heading>Practice Location</Styled.Heading>
          <DispensaryAddressForm countriesList={countriesList} lang={lang} />
        </Styled.FormDiv>
        <Styled.Heading>Contact Picture</Styled.Heading>
        <ImageEditor
          sanityValue={sanityDispensary.contact?.image?.asset._ref}
          handleSubmit={handleImageChange}
          submitOnChange={true}
          aspectRatio="contact"
        />
        <input type="hidden" name="imageAssetRef" ref={register} />
        <Spacer size={3} />
        <InputTooLongModal
          className="shipping-restrictions-modal"
          handleClose={() => setOpen(false)}
          handleSubmit={handleModalSubmit}
          loading={isSubmitting}
          open={open}
        >
          <ModalMessage variant="body2" gutterBottom>
            The following fields are too long and might not display properly to customers:
          </ModalMessage>
          <TooLongItemList tooLongItems={overLengthFields} />
        </InputTooLongModal>
        <Styled.TrailingButtonContainer>{submitButton}</Styled.TrailingButtonContainer>
      </form>
    </FormProvider>
  )
}

const PractitionerDispensaryContact: React.FC<DispensaryAccountPageProps> = ({ dispensary }) => (
  <Subscribe fallback={<Spinner loading />}>
    <DispensaryContactReactive dispensary={dispensary} />
  </Subscribe>
)

export default withDispensaryAccount(PractitionerDispensaryContact)
